import "./GymListings.scss";
import React, { useEffect, useRef, useState } from "react";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAppDispatch from "../../hooks/useAppDispatch";
import { useGymListingMutations } from "../../api/mutations";
import { openSuccessMessage } from "../../store/message/messageSlice";
import { Form, FormInstance, GetProp, TableProps } from "antd";
import { Table } from "antd";
import searchIconWhite from "../../assets/images/icons/search-icon-white.svg";
import dots from "../../assets/images/icons/dots.svg";
import editIcon from "../../assets/images/icons/edit-dark.svg";
import viewIcon from "../../assets/images/icons/eye-dark.svg";
import basketIcon from "../../assets/images/icons/delete-red.svg";
import approveIcon from "../../assets/images/icons/approve.svg";
import declineIcon from "../../assets/images/icons/decline.svg";
import suspendIcon from "../../assets/images/icons/lock.svg";
import unsuspendIcon from "../../assets/images/icons/lock-opened.svg";
import assignIcon from "../../assets/images/icons/assign.svg";
import servicesIcon from "../../assets/images/icons/services.svg";
import approveBlackIcon from "../../assets/images/icons/approve-black.svg";
import declineBlackIcon from "../../assets/images/icons/decline-black.svg";
import { GymListingStatusEnum, IGymListing } from "../../models/GymListing";
import { GymListingService } from "../../services/gym-listing.service";
import FormItemDatePicker from "../../components/ui/FormItemDatePicker/FormItemDatePicker";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import FormItemSelect from "../../components/ui/FormItemSelect/FormItemSelect";
import ConfirmPopup from "../../components/ConfirmPopup/ConfirmPopup";
import DeclinePopup from "./DeclinePopup/DeclinePopup";
import AssignContractPopup from "../../components/AssignContractPopup/AssignContractPopup";
import { handleError } from "../../helper/errorHandler";
import FormItemInput from "../../components/ui/FormItemInput/FormItemInput";

type ColumnsType<T> = TableProps<T>["columns"];
type TablePaginationConfig = Exclude<GetProp<TableProps, "pagination">, boolean>;

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters?: Parameters<GetProp<TableProps, "onChange">>[1];
}

const GymListings = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const formRef = useRef<FormInstance>(null);
  const navigate = useNavigate();

  const {
    deleteGymListing,
    unsuspendGymListing,
    suspendGymListing,
    approveGymListing,
    enableLastMinuteDeal,
    disableLastMinuteDeal,
  } = useGymListingMutations();

  const [selectedIdForDelete, setSelectedIdForDelete] = useState<string | null>(null);
  const [selectedIdForApprove, setSelectedIdForApprove] = useState<string | null>(null);
  const [selectedIdForDecline, setSelectedIdForDecline] = useState<string | null>(null);
  const [selectedIdForSuspend, setSelectedIdForSuspend] = useState<string | null>(null);
  const [selectedIdForUnSuspend, setSelectedIdForUnSuspend] = useState<string | null>(null);
  const [selectedIdForEnableLastMinDeal, setSelectedIdForEnableLastMinDeal] = useState<
    string | null
  >(null);
  const [selectedIdForDisableLastMinDeal, setSelectedIdForDisableLastMinDeal] = useState<
    string | null
  >(null);
  const [selectedIdForAssign, setSelectedIdForAssign] = useState<string | null>(null);

  const [data, setData] = useState<IGymListing[]>();
  const [loading, setLoading] = useState(false);
  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  useEffect(() => {
    getGymListing(tableParams.pagination?.current, tableParams.pagination?.pageSize);
  }, [tableParams.pagination?.current, tableParams.pagination?.pageSize]);

  const handleTableChange: TableProps<IGymListing>["onChange"] = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
    if (pagination.pageSize !== tableParams.pagination?.pageSize) {
      setData([]);
    }
  };

  const columns: ColumnsType<IGymListing> = [
    {
      title: t("GymName"),
      render: (data: IGymListing) => {
        return data.businessName;
      },
    },
    {
      title: t("LicenseGymName"),
      render: (data: IGymListing) => {
        return (
          <a href={data.webEndpoint} target={"_blank"}>
            {data.licenseGymName}
          </a>
        );
      },
    },
    {
      title: t("Location"),
      render: (data: IGymListing) => {
        return `${data.country}, ${data.city}, ${data.street} ${data.streetNumber}, ${data.postcode}`;
      },
    },
    {
      title: t("Contract"),
      render: (data: IGymListing) => {
        if (!data.gymListingContracts.length)
          return <div style={{ color: "#D93131" }}>{t("NoContractAssigned")}</div>;
        return (
          <>
            {data.gymListingContracts.map((contract, index) => {
              if (!contract.assignedAt)
                return (
                  <div key={contract.contractVersionId}>
                    {contract.contractVersion.contract.title}, v{contract.contractVersion.version}{" "}
                    <span style={{ color: "#D93131" }}>({t("NotSigned")})</span>
                    {index !== data.gymListingContracts.length - 1 ? " | " : ""}
                  </div>
                );
              return (
                <div key={contract.contractVersionId}>
                  {contract.contractVersion.contract.title}, v{contract.contractVersion.version}
                  {index !== data.gymListingContracts.length - 1 ? " | " : ""}
                </div>
              );
            })}
          </>
        );
      },
    },
    {
      title: t("SuspensionStatus"),
      render: (data: IGymListing) => {
        if (data.isSuspended) {
          return <span className={"status declined"}>{t("Suspended")}</span>;
        } else {
          return <span className={"status approved"}>{t("UnSuspended")}</span>;
        }
      },
    },
    {
      title: t("LastMinDeal"),
      render: (data: IGymListing) => {
        if (data.lastMinDealEnabled) {
          return <span className={"status approved"}>{t("Enabled")}</span>;
        } else {
          return <span className={"status declined"}>{t("Disabled")}</span>;
        }
      },
    },
    {
      title: t("Status"),
      render: (data: IGymListing) => {
        if (data.status === GymListingStatusEnum.Approved) {
          return <span className={"status approved"}>{t("Approved")}</span>;
        }
        if (data.status === GymListingStatusEnum.Declined) {
          return <span className={"status declined"}>{t("Declined")}</span>;
        }
        return <span className={"status under-review"}>{t("UnderReview")}</span>;
      },
    },
    {
      title: t("Action"),
      render: (data: IGymListing) => {
        return (
          <div className={"action-wrapper"}>
            <img src={dots} alt={"action"} />
            <ul className={"dropdown"}>
              <li className={"dropdown-item"}>
                <img src={editIcon} alt={"edit"} />
                {t("Edit")}
              </li>
              <li
                className={"dropdown-item"}
                onClick={() => navigate(`/gym-listings/preview/${data.id}`)}
              >
                <img src={viewIcon} alt={"Preview"} />
                {t("Preview")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForDelete(data.id)}>
                <img src={basketIcon} alt={"delete"} />
                {t("Delete")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForApprove(data.id)}>
                <img src={approveIcon} alt={"approve"} />
                {t("Approve")}
              </li>
              <li className={"dropdown-item"} onClick={() => setSelectedIdForDecline(data.id)}>
                <img src={declineIcon} alt={"decline"} />
                {t("Decline")}
              </li>
              {data.isSuspended ? (
                <li className={"dropdown-item"} onClick={() => setSelectedIdForUnSuspend(data.id)}>
                  <img src={unsuspendIcon} alt={"unsuspend"} />
                  {t("UnSuspend")}
                </li>
              ) : (
                <li className={"dropdown-item"} onClick={() => setSelectedIdForSuspend(data.id)}>
                  <img src={suspendIcon} alt={"suspend"} />
                  {t("Suspend")}
                </li>
              )}
              {data.lastMinDealEnabled ? (
                <li
                  className={"dropdown-item"}
                  onClick={() => setSelectedIdForDisableLastMinDeal(data.id)}
                >
                  <img src={declineBlackIcon} alt={"disable"} />
                  {t("DisableLastMinDeal")}
                </li>
              ) : (
                <li
                  className={"dropdown-item"}
                  onClick={() => setSelectedIdForEnableLastMinDeal(data.id)}
                >
                  <img src={approveBlackIcon} alt={"enable"} />
                  {t("EnableLastMinDeal")}
                </li>
              )}
              <li className={"dropdown-item"} onClick={() => setSelectedIdForAssign(data.id)}>
                <img src={assignIcon} alt={"assign"} />
                {t("Assign")}
              </li>
              <li
                className={"dropdown-item"}
                onClick={() => navigate(`/gym-listings/classes/${data.id}`)}
              >
                <img src={servicesIcon} alt={"services"} />
                {t("Services")}
              </li>
            </ul>
          </div>
        );
      },
    },
  ];

  const getGymListing = async (
    currentPage: number | undefined,
    pageSize: number | undefined,
    queryOptions?: {
      from: string | null;
      to: string | null;
      status: string | null;
      searchValue: string | null;
    }
  ) => {
    try {
      setLoading(true);
      const response = await GymListingService.getGymListing(currentPage, pageSize, queryOptions);
      if (response.status === 200) {
        setLoading(false);
        setData(response.data.data);
        setTableParams({
          ...tableParams,
          pagination: {
            ...tableParams.pagination,
            total: response.data.totalCount,
          },
        });
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  const onFinish = (values: {
    from: Date | undefined;
    to: Date | undefined;
    status: string | undefined;
    searchValue: string | undefined;
  }) => {
    const model = {
      from: values.from ? values.from.toISOString() : null,
      to: values.to ? values.to.toISOString() : null,
      status: values.status ? values.status : null,
      searchValue: values.searchValue ? values.searchValue : null,
    };
    getGymListing(tableParams.pagination?.current, tableParams.pagination?.pageSize, model);
  };

  const selectItems = [
    { id: "Approved", label: t("Approved") },
    { id: "Declined", label: t("Declined") },
    { id: "UnderReview", label: t("UnderReview") },
  ];

  const deleteGymListingFn = async () => {
    if (!selectedIdForDelete) return;
    setLoading(true);
    deleteGymListing(selectedIdForDelete, {
      onSuccess: () => onSuccessFn("GymDeletedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const approveGymListingFn = async () => {
    if (!selectedIdForApprove) return;
    setLoading(true);
    approveGymListing(selectedIdForApprove, {
      onSuccess: () => onSuccessFn("GymApprovedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const suspendGymListingFn = async () => {
    if (!selectedIdForSuspend) return;
    setLoading(true);
    suspendGymListing(selectedIdForSuspend, {
      onSuccess: () => onSuccessFn("GymSuspendedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const unsuspendGymListingFn = async () => {
    if (!selectedIdForUnSuspend) return;
    setLoading(true);
    unsuspendGymListing(selectedIdForUnSuspend, {
      onSuccess: () => onSuccessFn("GymUnSuspendedSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const enableLastMinFn = async () => {
    if (!selectedIdForEnableLastMinDeal) return;
    setLoading(true);
    enableLastMinuteDeal(selectedIdForEnableLastMinDeal, {
      onSuccess: () => onSuccessFn("LastMinRulesEnabledSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const disableLastMinFn = async () => {
    if (!selectedIdForDisableLastMinDeal) return;
    setLoading(true);
    disableLastMinuteDeal(selectedIdForDisableLastMinDeal, {
      onSuccess: () => onSuccessFn("LastMinRulesDisabledSuccessfully"),
      onError: (e) => handleError(e, dispatch, t),
      onSettled: () => setLoading(false),
    });
  };

  const onSuccessFn = async (message: string) => {
    onClickNo();
    getGymListing(tableParams.pagination?.current, tableParams.pagination?.pageSize);
    if (formRef.current) {
      formRef.current.setFieldsValue({
        from: null,
        to: null,
        status: null,
      });
    }
    dispatch(openSuccessMessage(t(message)));
  };

  const onClickNo = () => {
    setSelectedIdForDelete(null);
    setSelectedIdForApprove(null);
    setSelectedIdForSuspend(null);
    setSelectedIdForUnSuspend(null);
    setSelectedIdForEnableLastMinDeal(null);
    setSelectedIdForDisableLastMinDeal(null);
  };

  return (
    <>
      <div className={"page-content gyms-page"}>
        <MainTitle text={t("GymListings")} />

        <div className={"top-row"}>
          <Form
            className={"form"}
            ref={formRef}
            name="gym-listing-filter"
            onFinish={onFinish}
            autoComplete="off"
            layout={"vertical"}
          >
            <FormItemInput
              name={"searchValue"}
              label={t("Search")}
              rules={{ required: false }}
              placeholder={t("SearchByLicenseName")}
              style={{
                width: "30%",
              }}
            />
            <FormItemDatePicker
              dateFormat={"DD/MM/YYYY"}
              name={"from"}
              label={t("From")}
              rules={{ required: false }}
              style={{
                width: "20%",
              }}
            />
            <FormItemDatePicker
              dateFormat={"DD/MM/YYYY"}
              name={"to"}
              label={t("To")}
              rules={{ required: false }}
              style={{
                width: "20%",
              }}
            />
            <FormItemSelect
              name={"status"}
              label={t("Status")}
              rules={{ required: false }}
              items={selectItems}
              placeholder={t("SelectStatus")}
              style={{
                width: "20%",
              }}
            />
            <div className={"btn-wrapper"}>
              <CustomButton
                text={""}
                type={"submit"}
                typeStyle={"fill"}
                icon={searchIconWhite}
                altIcon={"search"}
              />
            </div>
          </Form>
        </div>

        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
      {selectedIdForDelete && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteThisGym")}
          onClickYes={deleteGymListingFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedIdForApprove && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToApproveThisGymListing")}
          onClickYes={approveGymListingFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedIdForDecline && (
        <DeclinePopup
          close={() => setSelectedIdForDecline(null)}
          id={selectedIdForDecline}
          getData={() =>
            getGymListing(tableParams.pagination?.current, tableParams.pagination?.pageSize)
          }
          setLoading={setLoading}
        />
      )}
      {selectedIdForSuspend && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToSuspendThisGymListing")}
          onClickYes={suspendGymListingFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedIdForUnSuspend && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToUnSuspendThisGymListing")}
          onClickYes={unsuspendGymListingFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedIdForAssign && (
        <AssignContractPopup
          closeFn={() => setSelectedIdForAssign(null)}
          gymListingId={selectedIdForAssign}
          getData={() =>
            getGymListing(tableParams.pagination?.current, tableParams.pagination?.pageSize)
          }
        />
      )}
      {selectedIdForEnableLastMinDeal && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToEnableLastMinRulesForThisGym")}
          onClickYes={enableLastMinFn}
          onClickNo={onClickNo}
        />
      )}
      {selectedIdForDisableLastMinDeal && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDisableLastMinRulesForThisGym")}
          onClickYes={disableLastMinFn}
          onClickNo={onClickNo}
        />
      )}
    </>
  );
};

export default GymListings;
